/**
 * commandPaletteSlice — global command palette UI state.
 *
 * Manages open/close, the current search query, and the list of recently
 * invoked command IDs (non-PII strings like route paths or action keys).
 *
 * RULES:
 * - No server data. Commands are resolved by the consuming app from route/RBAC context.
 * - recentCommandIds contains only stable command identifier strings — no user input, no labels.
 * - Max 10 recent commands kept to bound memory.
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

const MAX_RECENT = 10;

export interface CommandPaletteState {
  open: boolean;
  query: string;
  /** Ordered list of recently used command IDs (most recent first). Non-PII stable keys. */
  recentCommandIds: string[];
}

const initialState: CommandPaletteState = {
  open: false,
  query: '',
  recentCommandIds: [],
};

export const commandPaletteSlice = createSlice({
  name: 'commandPalette',
  initialState,
  reducers: {
    openCommandPalette(state) {
      state.open = true;
    },
    closeCommandPalette(state) {
      state.open = false;
      state.query = '';
    },
    toggleCommandPalette(state) {
      state.open = !state.open;
      if (!state.open) {
        state.query = '';
      }
    },
    setCommandPaletteQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },
    /**
     * Record a command ID as recently used.
     * Deduplicates and limits to MAX_RECENT entries.
     * commandId must be a stable, non-PII string (e.g. 'nav.live-calls', 'action.invite-member').
     */
    recordRecentCommand(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.recentCommandIds = [id, ...state.recentCommandIds.filter((r) => r !== id)].slice(
        0,
        MAX_RECENT,
      );
    },
    clearRecentCommands(state) {
      state.recentCommandIds = [];
    },
  },
});

export const {
  openCommandPalette,
  closeCommandPalette,
  toggleCommandPalette,
  setCommandPaletteQuery,
  recordRecentCommand,
  clearRecentCommands,
} = commandPaletteSlice.actions;

// Selectors
export const selectCommandPaletteOpen = (state: { commandPalette: CommandPaletteState }) =>
  state.commandPalette.open;

export const selectCommandPaletteQuery = (state: { commandPalette: CommandPaletteState }) =>
  state.commandPalette.query;

export const selectRecentCommandIds = (state: { commandPalette: CommandPaletteState }) =>
  state.commandPalette.recentCommandIds;
